import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RotateCw, ArrowLeft } from 'lucide-react';
import { Sentry } from '@/lib/sentry';

type Props = {
  children: ReactNode;
  /** Boundary-tag voor Sentry-grouping (bv. 'lpd-editor', 'lpd-kavel'). */
  area?: string;
  /** Optioneel pad waar "Terug" naar verwijst (default: /laadplein-designer). */
  backHref?: string;
};

type State = { error: Error | null };

export class LaadpleinErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      tags: { feature: 'lpd', boundary: this.props.area ?? 'lpd-unknown' },
      extra: { componentStack: info.componentStack },
    });
    if (typeof console !== 'undefined' && console.error) {
      console.error(`[lpd] ErrorBoundary (${this.props.area ?? 'unknown'}) caught:`, error);
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const backHref = this.props.backHref ?? '/project-builder';
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-100/95 backdrop-blur-sm p-6">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-md w-full p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-100 p-2 shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-semibold">Editor crashed</h2>
              <p className="text-sm text-muted-foreground">
                Er ging iets mis. De fout is automatisch gerapporteerd. Je kunt
                het opnieuw proberen of de pagina herladen.
              </p>
            </div>
          </div>
          <details className="text-[11px] bg-gray-50 rounded-md p-2 border">
            <summary className="cursor-pointer text-muted-foreground select-none">
              Technische details
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-gray-700">
              {this.state.error.message}
            </pre>
          </details>
          <div className="flex flex-wrap gap-2">
            <Button onClick={this.reload} size="sm">
              <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Pagina herladen
            </Button>
            <Button onClick={this.reset} variant="outline" size="sm">
              Opnieuw proberen
            </Button>
            <Button asChild variant="ghost" size="sm">
              <a href={backHref}>
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Terug naar lijst
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
